import React from "react";
import { Box, Paper, IconButton, Typography, Tooltip } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

/**
 * Wraps a board column so it can collapse to a thin, clickable rail and expand
 * back to a full-width flex column. `side` controls which way the chevrons
 * point ("left" = a left-hand column that tucks away to the left).
 *
 * Collapsed: a narrow Paper rail with an expand button and a vertical label.
 * Expanded: the children, with a small collapse button in the top corner.
 */
export default function CollapsibleColumn({
  side = "left",
  label,
  collapsed,
  onToggle,
  children,
}) {
  if (collapsed) {
    return (
      <Tooltip title={`Show ${label}`} placement={side === "left" ? "right" : "left"}>
        <Paper
          variant="outlined"
          onClick={onToggle}
          sx={{
            flex: "0 0 44px",
            minWidth: 44,
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 1,
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <IconButton size="small" aria-label={`Show ${label}`}>
            {side === "left" ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>

          <Typography
            variant="body2"
            sx={{
              writingMode: "vertical-rl",
              transform: "rotate(180deg)",
              mt: 1,
              fontWeight: 700,
              opacity: 0.75,
              whiteSpace: "nowrap",
              userSelect: "none",
            }}
          >
            {label}
          </Typography>
        </Paper>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ flex: "1 1 320px", minWidth: 0, position: "relative" }}>
      <Tooltip title={`Hide ${label}`}>
        <IconButton
          size="small"
          onClick={onToggle}
          aria-label={`Hide ${label}`}
          sx={{ position: "absolute", top: 6, right: 6, zIndex: 1 }}
        >
          {side === "left" ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Tooltip>

      {children}
    </Box>
  );
}
